import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError, formatDate, formatIDR } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash, UploadSimple, MagnifyingGlass, CircleNotch } from "@phosphor-icons/react";
import { DatePickerID } from "../components/DatePickerID";

const COVERAGE_LABELS = { all_risk: "All Risk", tlo: "TLO" };
const USAGE_LABELS = { pribadi_dinas: "Pribadi/Dinas", komersial_disewakan: "Komersial/Disewakan" };

const emptyForm = {
  policy_number: "", claim_type: "jiwa", policy_start_date: "", policy_end_date: "", holder_name: "",
  coverage_type: "", usage_type: "",
  insured_name: "", insured_age: "", vehicle_brand: "", plate_number: "", vehicle_year: "", vehicle_value: "",
};

export default function PoliciesPage() {
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/policies", { params: q ? { q } : {} }).then((r) => setPolicies(r.data)).catch(() => {});
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const create = async () => {
    try {
      const payload = {
        ...form,
        insured_age: form.insured_age ? Number(form.insured_age) : null,
        vehicle_year: form.vehicle_year ? Number(form.vehicle_year) : null,
        vehicle_value: form.vehicle_value ? Number(form.vehicle_value) : null,
      };
      await api.post("/policies", payload);
      toast.success(`Polis ${form.policy_number.toUpperCase()} tersimpan`);
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/policies/import", fd);
      toast.success(`Import selesai: ${data.created} baru, ${data.updated} diperbarui, ${data.skipped} dilewati`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const remove = async (id, policyNumber) => {
    if (!window.confirm(`Hapus polis ${policyNumber} dari master data?`)) return;
    try { await api.delete(`/policies/${id}`); toast.success("Polis dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8" data-testid="policies-page">
      <h1 className="text-2xl font-semibold tracking-tight">Master Data Polis</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">
        Ikhtisar polis dari sistem asuransi. Saat input klaim, nomor polis otomatis mengisi data — user cukup mengisi detail kejadian.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="policy-search" className="pl-9 w-72" placeholder="Cari no. polis / pemegang / plat"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex-1" />
        <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="policy-csv-input" onChange={importCsv} />
        <Button data-testid="import-csv-button" variant="outline" disabled={importing} onClick={() => fileRef.current?.click()}
          className="rounded-none gap-2">
          {importing ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />} Import CSV
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-policy-button" className="bg-slate-900 hover:bg-slate-800 rounded-none gap-2"><Plus size={16} /> Tambah Polis</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Ikhtisar Polis Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nomor Polis</Label>
                  <Input data-testid="policy-form-number" value={form.policy_number} onChange={(e) => setF("policy_number", e.target.value)} placeholder="POL-2026-XXXX" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Jenis</Label>
                  <Select value={form.claim_type} onValueChange={(v) => setF("claim_type", v)}>
                    <SelectTrigger data-testid="policy-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="jiwa">Jiwa</SelectItem><SelectItem value="kendaraan">Kendaraan</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tanggal Mulai Polis</Label>
                  <DatePickerID testId="policy-form-start-date" value={form.policy_start_date} onChange={(v) => setF("policy_start_date", v)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Pemegang Polis</Label>
                  <Input data-testid="policy-form-holder" value={form.holder_name} onChange={(e) => setF("holder_name", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tanggal Berakhir Polis</Label>
                  <DatePickerID testId="policy-form-end-date" value={form.policy_end_date} onChange={(v) => setF("policy_end_date", v)} />
                </div>
              </div>
              {form.claim_type === "jiwa" ? (
                <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Nama Tertanggung</Label>
                    <Input data-testid="policy-form-insured-name" value={form.insured_name} onChange={(e) => setF("insured_name", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Usia Tertanggung</Label>
                    <Input data-testid="policy-form-insured-age" type="number" value={form.insured_age} onChange={(e) => setF("insured_age", e.target.value)} />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Jenis Asuransi</Label>
                    <Select value={form.coverage_type} onValueChange={(v) => setF("coverage_type", v)}>
                      <SelectTrigger data-testid="policy-form-coverage"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_risk">All Risk — kerusakan/kehilangan sebagian & total</SelectItem>
                        <SelectItem value="tlo">TLO — hanya kerusakan/kehilangan ≥ 75% atau total</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Jenis Penggunaan</Label>
                    <Select value={form.usage_type} onValueChange={(v) => setF("usage_type", v)}>
                      <SelectTrigger data-testid="policy-form-usage"><SelectValue placeholder="Pilih penggunaan" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pribadi_dinas">Pribadi / Dinas</SelectItem>
                        <SelectItem value="komersial_disewakan">Komersial / Disewakan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Merek / Tipe Kendaraan</Label>
                    <Input data-testid="policy-form-vehicle-brand" value={form.vehicle_brand} onChange={(e) => setF("vehicle_brand", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Nomor Polisi</Label>
                    <Input data-testid="policy-form-plate" value={form.plate_number} onChange={(e) => setF("plate_number", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Tahun</Label>
                    <Input data-testid="policy-form-vehicle-year" type="number" value={form.vehicle_year} onChange={(e) => setF("vehicle_year", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Nilai Kendaraan (Rp)</Label>
                    <Input data-testid="policy-form-vehicle-value" type="number" value={form.vehicle_value} onChange={(e) => setF("vehicle_value", e.target.value)} />
                  </div>
                </div>
              )}
              <Button data-testid="policy-form-submit" onClick={create}
                disabled={!form.policy_number || !form.policy_start_date || !form.holder_name}
                className="w-full bg-slate-900 hover:bg-slate-800 rounded-none">Simpan Polis</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        Format CSV: kolom <span className="font-mono">policy_number, claim_type, policy_start_date, policy_end_date, holder_name, coverage_type (all_risk/tlo), usage_type (pribadi_dinas/komersial_disewakan), insured_name, insured_age, vehicle_brand, plate_number, vehicle_year, vehicle_value</span>
      </p>

      <div className="bg-card border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-5 py-3 font-medium">No. Polis</th>
              <th className="px-3 py-3 font-medium">Jenis</th>
              <th className="px-3 py-3 font-medium">Pemegang</th>
              <th className="px-3 py-3 font-medium">Mulai Polis</th>
              <th className="px-3 py-3 font-medium">Detail Objek</th>
              {user.role === "admin" && <th className="px-3 py-3 w-12"></th>}
            </tr>
          </thead>
          <tbody>
            {policies.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground" data-testid="empty-policies">Belum ada data polis. Tambah manual atau import CSV.</td></tr>
            )}
            {policies.map((p) => (
              <tr key={p.id} data-testid={`policy-row-${p.policy_number}`} className="border-b border-border/60 last:border-0 hover:bg-accent transition-colors duration-150">
                <td className="px-5 py-3 font-mono text-xs font-medium">{p.policy_number}</td>
                <td className="px-3 py-3 capitalize">{p.claim_type}</td>
                <td className="px-3 py-3">{p.holder_name}</td>
                <td className="px-3 py-3 text-xs">{formatDate(p.policy_start_date)}</td>
                <td className="px-3 py-3 text-xs text-muted-foreground">
                  {p.claim_type === "jiwa"
                    ? `${p.insured_name || "-"}${p.insured_age ? `, ${p.insured_age} thn` : ""}`
                    : [`${p.vehicle_brand || "-"} ${p.plate_number ? `(${p.plate_number})` : ""}`.trim(),
                       COVERAGE_LABELS[p.coverage_type], USAGE_LABELS[p.usage_type],
                       p.policy_end_date ? `s/d ${formatDate(p.policy_end_date)}` : null].filter(Boolean).join(" · ")}
                </td>
                {user.role === "admin" && (
                  <td className="px-3 py-3">
                    <button data-testid={`policy-delete-${p.policy_number}`} onClick={() => remove(p.id, p.policy_number)}
                      className="text-muted-foreground hover:text-red-600 transition-colors duration-150"><Trash size={16} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
